import { Flex } from "@chakra-ui/layout";
import { Box, Text, useDisclosure } from "@chakra-ui/react";
import {
	type ColumnDef,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";
import {
	useCenter,
	useCenterStat,
	useCenters,
} from "../../hooks/centers.hooks";
import { useCenterCommands } from "../../hooks/commands.hooks";
import { usePrepPurchaseOrder } from "../../hooks/purchase_order.hooks";
import { useSimpleToast } from "../../hooks/toast.hooks";
import { fetchCommandByExposedId } from "../../queries/command.queries";
import { type Command, CommandStatus } from "../../types/command.types";
import BrandFullCheckIcon from "../icons/BrandFullCheckIcon";
import BuildingOutlinedIcon from "../icons/BuildingOutlinedIcon";
import TrashIcon from "../icons/TrashIcon";
import OrderStatusBadge from "../OrderStatusBadge";
import ClientSideTable from "../table/ClientSideTable";
import SimpleModal from "./SimpleModal";

type PrepCommandModalProps = {
	isOpen: boolean;
	onClose: () => void;
	goBack: () => void;
	deliveryType: "UPS" | "OTHER" | null;
};

type MissingReadyForDeliveryCommand = Pick<Command, "id" | "exposedId">;

const COMMAND_STATUSES_VISIBLE_FOR_PARCEL_PREPARATION = new Set<CommandStatus>([
	CommandStatus.WAITING_FOR_MODELING,
	CommandStatus.MODELING,
	CommandStatus.WAITING_FOR_PRINTING,
	CommandStatus.PRINTING,
	CommandStatus.PRINTED,
	CommandStatus.WAITING_FOR_LAB,
	CommandStatus.INJECTING,
	CommandStatus.POLISHING,
	CommandStatus.QUALITY_CONTROL,
	CommandStatus.READY,
	CommandStatus.READY_FOR_DELIVERY,
]);

const isCommandVisibleForParcelPreparation = (command: Command) => {
	return COMMAND_STATUSES_VISIBLE_FOR_PARCEL_PREPARATION.has(command.status);
};

const isCommandSelectableForParcelPreparation = (command: Command) => {
	return command.status === CommandStatus.READY_FOR_DELIVERY;
};

const PrepCommandModal = ({
	isOpen,
	onClose,
	goBack,
	deliveryType,
}: PrepCommandModalProps) => {
	const [commands, setCommands] = useState<Command[]>([]);
	const [missingReadyForDeliveryCommands, setMissingReadyForDeliveryCommands] =
		useState<MissingReadyForDeliveryCommand[]>([]);
	const missingReadyCommandsDisclosure = useDisclosure();
	const centerId = commands.reduce<undefined | string>((prev, command) => {
		return prev ?? command?.centerId?.toString();
	}, undefined);
	const centerState = useCenter(centerId);
	const centerCommandsState = useCenterCommands(centerId);
	const centerCommands = useMemo(() => {
		if (!isOpen || !centerId) {
			return [];
		}
		return (centerCommandsState?.data ?? []).filter(
			isCommandVisibleForParcelPreparation,
		);
	}, [centerCommandsState?.data, centerId, isOpen]);
	const toast = useSimpleToast();
	const handleOnClose = () => {
		setCommands([]);
		setMissingReadyForDeliveryCommands([]);
		missingReadyCommandsDisclosure.onClose();
		onClose();
	};
	const handleRemoveItem = useCallback((command: Command) => {
		setCommands((currentCommands) =>
			currentCommands.filter((sc) => sc.id !== command.id),
		);
	}, []);
	const commandCode = useRef("");
	const handleOnPaste = useCallback(
		async (event: React.ClipboardEvent) => {
			try {
				// 1. Parse code from clipboard
				const text = event.clipboardData.getData("text");
				// 2. Ensure not already in list
				if (
					commands.findIndex(
						(command) => command.exposedId.toString() === text,
					) !== -1
				) {
					toast.info("Scan", "Le code est déjà présent dans la liste.");
					return;
				}
				// 3. Try to retrieve from backend
				const command = await fetchCommandByExposedId(text);
				if (command !== null) {
					if (!isCommandVisibleForParcelPreparation(command)) {
						toast.info(
							"Scan",
							"Cette commande n'est pas éligible à la préparation du colis",
						);
						return;
					}
					if (!isCommandSelectableForParcelPreparation(command)) {
						toast.info("Scan", "Cette commande n'est pas prête à être livrée");
						return;
					}
					if (centerId && command.centerId.toString() !== centerId) {
						toast.info(
							"Scan",
							"Seules les commandes du même centre peuvent être ajoutées",
						);
						return;
					}
					setCommands([command, ...commands]);
				}
			} catch (_err) {
				toast.error("Scan", "Le code n'a pas pu être lu.");
			}
		},
		[centerId, commands, toast],
	);
	const handleGoBack = useCallback(() => {
		setCommands([]);
		setMissingReadyForDeliveryCommands([]);
		missingReadyCommandsDisclosure.onClose();
		goBack();
	}, [goBack, missingReadyCommandsDisclosure]);
	const handleCloseMissingReadyCommandsModal = useCallback(() => {
		setMissingReadyForDeliveryCommands([]);
		missingReadyCommandsDisclosure.onClose();
	}, [missingReadyCommandsDisclosure]);
	const handleOnKeyDown = async (event: React.KeyboardEvent) => {
		switch (event.code) {
			case "Enter": {
				const code = commandCode.current.toLowerCase().split("§").join("-");
				commandCode.current = "";
				if (
					commands.findIndex(
						(command) => command.exposedId.toString() === code,
					) !== -1
				) {
					toast.info("Scan", "Le code est déjà présent dans la liste.");
					return;
				}
				const command = await fetchCommandByExposedId(code);
				if (command === null) {
					toast.info("Scan", `Pas de commande trouvé pour le code '${code}'`);
					return;
				}
				if (!isCommandVisibleForParcelPreparation(command)) {
					toast.info(
						"Scan",
						"Cette commande n'est pas éligible à la préparation du colis",
					);
					return;
				}
				if (!isCommandSelectableForParcelPreparation(command)) {
					toast.info("Scan", "Cette commande n'est pas prête à être livrée");
					return;
				}
				if (centerId && command.centerId.toString() !== centerId) {
					toast.info(
						"Scan",
						"Seules les commandes du même centre peuvent être ajoutées",
					);
					return;
				}
				setCommands([command, ...commands]);
				break;
			}
			case "Shift":
			case "Meta":
			case "ShiftLeft":
				break;
			default: {
				commandCode.current = commandCode.current + event.key;
				break;
			}
		}
	};
	const handleAddCommand = (command: Command) => {
		const found = commands.find((c) => c.id === command.id);
		if (found) {
			setCommands((commands) => commands.filter((c) => c.id !== command.id));
		} else if (isCommandSelectableForParcelPreparation(command)) {
			setCommands((commands) => [...commands, command]);
		} else {
			toast.info(
				"Cette commande ne peut pas être ajoutée",
				`La commande ${command.exposedId} n'est pas prête à être livrée`,
			);
		}
	};
	const getMissingReadyForDeliveryCommands = useCallback(() => {
		const selectedCommandIds = new Set(commands.map((command) => command.id));
		return centerCommands
			.filter((command) => {
				return (
					command.status === CommandStatus.READY_FOR_DELIVERY &&
					!selectedCommandIds.has(command.id)
				);
			})
			.map((command) => ({
				id: command.id,
				exposedId: command.exposedId,
			}));
	}, [centerCommands, commands]);
	const columns = useMemo<ColumnDef<Command>[]>(
		() => [
			{
				accessorFn: (row) => row.exposedId,
				id: "exposed_id",
				header: "ID",
				cell: ({ row }) => (
					<div className="grid grid-cols-[30px_1fr] items-center">
						{commands.find((command) => command.id === row.original.id) ? (
							<BrandFullCheckIcon className="text-brand-600!" />
						) : (
							<Box></Box>
						)}
						<Text>{row.original.exposedId}</Text>
					</div>
				),
			},
			{
				accessorFn: (row) => {
					if (row.patient) {
						return `${row.patient.firstName} ${row.patient.lastName}`;
					}
					return `---`;
				},
				id: "patient",
				header: "Patient",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) =>
					row.groupExpectedShippingDate ?? row.expectedShippingDate,
				id: "created_at",
				header: "Date d'expédition",
				cell: ({ row }) => {
					const date =
						row.original.groupExpectedShippingDate ??
						row.original.expectedShippingDate;
					if (!date) return <Text>---</Text>;
					return <Text>{format(new Date(date), "dd/MM/yyyy")}</Text>;
				},
			},
			{
				accessorFn: (row) => (
					<OrderStatusBadge
						halted={row.haltedAt != null}
						status={row.status}
					></OrderStatusBadge>
				),
				id: "status",
				header: "Statut",
				cell: (info) => info.getValue(),
			},
			{
				accessorFn: (row) => (
					<Flex justifyContent={"end"}>
						<TrashIcon
							color={"gray.500"}
							onClick={() => handleRemoveItem(row)}
						></TrashIcon>
					</Flex>
				),
				id: "more",
				header: "",
				cell: (info) => info.getValue(),
			},
		],
		[commands, handleRemoveItem],
	);
	const table = useReactTable({
		data: centerCommands,
		columns: columns,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});
	const { center } = useCenters();
	const centerStats = useCenterStat(center?.id);
	const prepPurchaseOrder = usePrepPurchaseOrder({
		onSuccess: async () => {
			handleOnClose();
			toast.success("Bon de livraison", "Le bon de livraison a bien été créé");
		},
		onError: () => {
			toast.error(
				"Bon de livraison",
				"Erreur lors de la création du bon de livraison",
			);
		},
	});
	const handleCreatePurchaseOrder = useCallback(() => {
		if (!center || commands.length === 0) {
			return;
		}
		prepPurchaseOrder.mutate({
			centerId: center.id,
			commands: commands.map((command) => command.id),
			deliveryType: deliveryType,
		});
	}, [center, commands, deliveryType, prepPurchaseOrder]);
	const handleCreatePurchaseOrderAfterValidation = useCallback(() => {
		if (!center || commands.length === 0) {
			return;
		}
		const missingCommands = getMissingReadyForDeliveryCommands();
		if (missingCommands.length > 0) {
			setMissingReadyForDeliveryCommands(missingCommands);
			missingReadyCommandsDisclosure.onOpen();
			return;
		}
		handleCreatePurchaseOrder();
	}, [
		center,
		commands,
		getMissingReadyForDeliveryCommands,
		handleCreatePurchaseOrder,
		missingReadyCommandsDisclosure,
	]);
	const handleCreatePurchaseOrderIgnoringValidation = useCallback(() => {
		setMissingReadyForDeliveryCommands([]);
		missingReadyCommandsDisclosure.onClose();
		handleCreatePurchaseOrder();
	}, [handleCreatePurchaseOrder, missingReadyCommandsDisclosure]);
	return (
		<>
			<SimpleModal
				isOpen={isOpen}
				onClose={handleOnClose}
				w="min(1800px, 80vw)"
				title="Préparation du colis"
				abortText="Retour"
				onAbort={handleGoBack}
				onPaste={handleOnPaste}
				onKeyDown={handleOnKeyDown}
				isPending={prepPurchaseOrder.isLoading}
				onClick={handleCreatePurchaseOrderAfterValidation}
			>
				<ClientSideTable
					table={table}
					w={["240px", "auto", "200px", "300px", "10px"]}
					flexGrow={[0, 1, 0, 0, 0]}
					onRowClick={(row) => {
						handleAddCommand(row as Command);
					}}
					onRowHighlight={(row) => {
						const currentCommand = row as Command;
						const found = commands.find(
							(command) => command.id === currentCommand.id,
						);

						if (found) {
							return "bg-brand-50!";
						}
						return "";
					}}
					actionsComponent={() => {
						if (centerState.isLoading || centerState.data == null) return null;
						return (
							<Box className="flex flex-row items-center gap-2">
								<BuildingOutlinedIcon
									w="12px"
									h="12px"
									className="text-brand-600!"
								/>
								<Text
									fontSize="12px"
									fontWeight={500}
									className="text-brand-600"
								>
									{centerState.data.id}
								</Text>
								<Text
									fontSize="12px"
									fontWeight={500}
									className="text-brand-600"
								>
									{centerState.data.name}
								</Text>
								{centerState && (
									<Box
										px={3}
										rounded="xl"
										border="1px solid"
										borderColor="gray.100"
										backgroundColor="gray.50"
									>
										<Text fontSize="12px" fontWeight={500} color="gray.700">
											{centerStats.data?.commandsCount} commandes
										</Text>
									</Box>
								)}
							</Box>
						);
					}}
				></ClientSideTable>
			</SimpleModal>
			<SimpleModal
				isOpen={missingReadyCommandsDisclosure.isOpen}
				onClose={handleCloseMissingReadyCommandsModal}
				w="min(900px, 80vw)"
				title="Commandes prêtes non sélectionnées"
				acceptText="Créer quand même"
				abortText="Retour"
				onAbort={handleCloseMissingReadyCommandsModal}
				onClick={handleCreatePurchaseOrderIgnoringValidation}
				isPending={prepPurchaseOrder.isLoading}
			>
				<Box className="flex flex-col gap-4">
					<Text color="gray.700">
						Certaines commandes de ce centre sont prêtes à être livrées mais
						n'ont pas été sélectionnées.
					</Text>
					<Text color="gray.700">
						Veuillez les sélectionner avant de créer le colis.
					</Text>
					<Box
						className="max-h-[260px] overflow-y-auto"
						border="1px solid"
						borderColor="gray.100"
						rounded="lg"
						px={4}
						py={3}
						backgroundColor="gray.50"
					>
						{missingReadyForDeliveryCommands.map((command) => {
							return (
								<Text key={command.id} color="gray.800" fontWeight={500}>
									{command.exposedId}
								</Text>
							);
						})}
					</Box>
				</Box>
			</SimpleModal>
		</>
	);
};

export default PrepCommandModal;
